/**
 * AppearanceContext — global appearance preference (density + light/dark/auto mode).
 *
 * Portal v2 (DEC-063): persists {density, mode} to localStorage under
 * `<platform>.appearance`. Publishes data-theme + data-density attributes on
 * documentElement so CSS consumers (module skins) can key off them.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { PORTAL_META } from '../registry/portalMeta';

// ── Types ──────────────────────────────────────────────────────────────────────

export type Density = 'compact' | 'comfortable' | 'wide';
export type AppearanceMode = 'light' | 'dark';
export type AppearanceSetting = AppearanceMode | 'auto';

interface AppearanceContextValue {
  density: Density;
  mode: AppearanceSetting;
  resolvedMode: AppearanceMode;
  setDensity: (d: Density) => void;
  setMode: (m: AppearanceSetting) => void;
}

// ── Storage ────────────────────────────────────────────────────────────────────

const STORAGE_KEY = `${PORTAL_META.platformName}.appearance`;
const DEFAULT_DENSITY: Density = 'comfortable';
const DEFAULT_MODE: AppearanceSetting = 'light';

interface StoredAppearance {
  density: Density;
  mode: AppearanceSetting;
}

function loadAppearance(): StoredAppearance {
  if (typeof window === 'undefined') return { density: DEFAULT_DENSITY, mode: DEFAULT_MODE };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<StoredAppearance>;
      const density: Density =
        parsed.density === 'compact' || parsed.density === 'comfortable' || parsed.density === 'wide'
          ? parsed.density
          : DEFAULT_DENSITY;
      const mode: AppearanceSetting =
        parsed.mode === 'light' || parsed.mode === 'dark' || parsed.mode === 'auto'
          ? parsed.mode
          : DEFAULT_MODE;
      return { density, mode };
    }
  } catch {
    // ignore storage failures — fall through to defaults
  }
  return { density: DEFAULT_DENSITY, mode: DEFAULT_MODE };
}

function saveAppearance(density: Density, mode: AppearanceSetting): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ density, mode }));
  } catch {
    // ignore
  }
}

function resolveMode(setting: AppearanceSetting): AppearanceMode {
  if (setting !== 'auto') return setting;
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

// ── Context ────────────────────────────────────────────────────────────────────

const AppearanceContext = createContext<AppearanceContextValue | null>(null);

export const AppearanceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [density, setDensityState] = useState<Density>(() => loadAppearance().density);
  const [mode, setModeState] = useState<AppearanceSetting>(() => loadAppearance().mode);
  const [resolvedMode, setResolvedMode] = useState<AppearanceMode>(() => resolveMode(loadAppearance().mode));

  // Listen for system preference changes when mode === 'auto'
  useEffect(() => {
    if (mode !== 'auto') {
      setResolvedMode(mode);
      return;
    }
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    setResolvedMode(mq.matches ? 'dark' : 'light');

    const handler = (e: { matches: boolean }) => {
      setResolvedMode(e.matches ? 'dark' : 'light');
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [mode]);

  // Expose data-density + data-theme on documentElement for CSS consumers
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.dataset.density = density;
    }
  }, [density]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.dataset.theme = resolvedMode;
    }
  }, [resolvedMode]);

  const setDensity = useCallback((d: Density) => {
    setDensityState(d);
    setModeState(prev => {
      saveAppearance(d, prev);
      return prev;
    });
  }, []);

  const setMode = useCallback((m: AppearanceSetting) => {
    setModeState(m);
    setDensityState(prev => {
      saveAppearance(prev, m);
      return prev;
    });
  }, []);

  const value = useMemo<AppearanceContextValue>(
    () => ({ density, mode, resolvedMode, setDensity, setMode }),
    [density, mode, resolvedMode, setDensity, setMode],
  );

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
};

export function useAppearance(): AppearanceContextValue {
  const ctx = useContext(AppearanceContext);
  if (!ctx) {
    throw new Error('useAppearance must be used inside <AppearanceProvider>');
  }
  return ctx;
}

// ── DensityTokens (consumed by portalTheme) ────────────────────────────────────

export interface DensityTokens {
  tableSize: 'small' | 'medium';
  cellPaddingY: number;
  cellPaddingX: number;
  headerFontSize: string;
  bodyFontSize: string;
}

export function densityTokens(d: Density): DensityTokens {
  switch (d) {
    case 'compact':
      return { tableSize: 'small',  cellPaddingY: 4,  cellPaddingX: 10, headerFontSize: '0.72rem',  bodyFontSize: '0.8125rem' };
    case 'comfortable':
      return { tableSize: 'small',  cellPaddingY: 8,  cellPaddingX: 14, headerFontSize: '0.75rem',  bodyFontSize: '0.875rem'  };
    case 'wide':
      return { tableSize: 'medium', cellPaddingY: 14, cellPaddingX: 16, headerFontSize: '0.78rem',  bodyFontSize: '0.9rem'    };
  }
}
